import React, { FC, useMemo, useState } from 'react';
import { Button, Input, Select, Tooltip } from 'antd';
import { ArrowDownShort, Bin, Close, CloseGreen, Plus } from 'common/svg';
import { Divider } from 'common/components';
import { IInventoryAssigneeShort } from 'common/models';
import { getId } from 'common/helpers';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { getInventoryAssigneeOptions } from 'entities/Inventory/Inventory.helper';
import { InventoryCreateAssigneeModal, InventoryDeleteAssigneeModal } from 'entities/Inventory/components';
import { createInventoryAssignee, deleteInventoryAssignee, selectIsInventoryLoading } from 'entities/Inventory/Inventory.slice';

interface IAntdFormControl {
  onChange?: (value?: any) => void;
  value?: any;
}

interface IComponentProps {
  assignees: IInventoryAssigneeShort[];
  className?: string;
  popupClassName?: string;
  allowClear?: boolean;
}

type AllType = IComponentProps & IAntdFormControl;

export const InventoryAssigneeSelector: FC<AllType> = ({
  // form
  value,
  onChange,
  // props
  assignees = [],
  className,
  popupClassName,
  allowClear = false,
}) => {
  const dispatch = useAppDispatch();
  // inventory
  const isInventoryLoading = useAppSelector(selectIsInventoryLoading);

  const [search, setSearch] = useState<string>('');
  const [deletingAssigneeId, setDeletingAssigneeId] = useState<number | null>(null);
  const [openInventoryAssigneeSelector, setOpenInventoryAssigneeSelector] = useState<boolean>(false);
  const [openInventoryAddAssigneeModal, setOpenInventoryAddAssigneeModal] = useState<boolean>(false);
  const [openInventoryDeleteAssigneeModal, setOpenInventoryDeleteAssigneeModal] = useState<boolean>(false);

  const assigneeOptions = useMemo(() => getInventoryAssigneeOptions(assignees, search), [assignees, search]);

  const toggleOpenAssigneeSelector = (value: boolean) => {
    if (value) {
      setSearch('');
    }

    setOpenInventoryAssigneeSelector(value);
  };

  const openAddAssigneeModal = () => {
    setOpenInventoryAddAssigneeModal(true);
  };

  const closeAddAssigneeModal = () => {
    setOpenInventoryAssigneeSelector(true);
    setOpenInventoryAddAssigneeModal(false);
  };

  const handleCreateInventoryAssignee = async (value: string, onSuccess: () => void) => {
    const assignee = await dispatch(createInventoryAssignee({ name: value, onSuccess }));

    if (assignee && onChange) {
      onChange(assignee.id);
    }
  };

  const openDeleteAssigneeModal = (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    setDeletingAssigneeId(id);
    setOpenInventoryDeleteAssigneeModal(true);
  };

  const closeDeleteAssigneeModal = () => {
    setOpenInventoryAssigneeSelector(true);
    setOpenInventoryDeleteAssigneeModal(false);
    setDeletingAssigneeId(null);
  };

  const handleDeleteInventoryAssignee = () => {
    if (!deletingAssigneeId) {
      return;
    }

    dispatch(
      deleteInventoryAssignee({
        id: deletingAssigneeId,
        onSuccess: () => {
          if (deletingAssigneeId === value && onChange) {
            onChange(null);
          }

          setOpenInventoryAssigneeSelector(true);
          setOpenInventoryDeleteAssigneeModal(false);
          setDeletingAssigneeId(null);
        },
      }),
    );
  };

  return (
    <>
      <Select
        open={openInventoryAssigneeSelector || openInventoryAddAssigneeModal || openInventoryDeleteAssigneeModal}
        onDropdownVisibleChange={toggleOpenAssigneeSelector}
        dropdownStyle={{ zIndex: 1001 }}
        rootClassName="redesign"
        className={className}
        popupClassName={popupClassName}
        placeholder="Выберите сотрудника"
        placement="bottomRight"
        virtual={false}
        suffixIcon={<ArrowDownShort />}
        notFoundContent="В списке нет вариантов."
        optionLabelProp="label"
        value={value}
        onChange={onChange}
        allowClear={allowClear ? { clearIcon: <CloseGreen /> } : false}
        dropdownRender={(menu) => {
          return (
            <>
              <div className="mb-6">
                <Input
                  allowClear={{ clearIcon: <Close className="icon-close-input" /> }}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              <Button
                className="button-text dark-green"
                icon={<Plus className="icon-plus-dark-green" />}
                onClick={openAddAssigneeModal}
              >
                Добавить сотрудника
              </Button>

              <Divider marginY={20} />

              {menu}
            </>
          );
        }}
      >
        {assigneeOptions.map((option) => {
          const selected = option.value === value;

          if (option.type === 'divider') {
            return <Select.Option key={getId()} type="divider" children={null} />;
          }

          return (
            <Select.Option key={option.value} value={option.value} label={option.label}>
              <div className="flex align-center justify-between">
                <span className={`${selected ? 'ff-medium' : 'ff-regular'} fs-m`}>{option.label}</span>
                <Tooltip title="Удалить" placement="bottom">
                  <Button
                    className="button-icon"
                    icon={<Bin className="icon-pale-red" />}
                    onClick={(e) => openDeleteAssigneeModal(e, option.value as number)}
                  />
                </Tooltip>
              </div>
            </Select.Option>
          );
        })}
      </Select>

      <InventoryCreateAssigneeModal
        open={openInventoryAddAssigneeModal}
        loading={isInventoryLoading}
        onClose={closeAddAssigneeModal}
        onSave={handleCreateInventoryAssignee}
      />

      <InventoryDeleteAssigneeModal
        open={openInventoryDeleteAssigneeModal}
        loading={isInventoryLoading}
        onCancel={closeDeleteAssigneeModal}
        onSubmit={handleDeleteInventoryAssignee}
      />
    </>
  );
};
